import { createPortal } from "react-dom";
// In-app notification centre (top banner). Polls every 30s; shows an unread
// badge; dropdown lists recent quote activity for me (AM/collaborator) with
// click-to-open and X-to-dismiss. Built to carry AI prompts / task nudges later.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, FileText, StickyNote, CheckCheck, History, RotateCcw, CalendarClock, AtSign } from "lucide-react";
import InboxV2 from "./notifications/InboxV2";
import api from "../lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.round((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [big, setBig] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [digestPending, setDigestPending] = useState(false);
  const [tab, setTab] = useState("inbox");
  const [history, setHistory] = useState([]);
  const navigate = useNavigate();
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
      setDigestPending(!!data.digest_pending);
    } catch { /* non-fatal */ }
  }, []);
  const loadHistory = useCallback(async () => {
    try { const { data } = await api.get("/notifications/history"); setHistory(data.items || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { if (open && tab === "history") loadHistory(); }, [open, tab, loadHistory]);
  const restore = async (n) => { await api.post(`/notifications/${n._id}/restore`); await Promise.all([load(), loadHistory()]); };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // quiet 30s refresh
    window.addEventListener("t2-notifications-refresh", load);
    return () => { clearInterval(t); window.removeEventListener("t2-notifications-refresh", load); };
  }, [load]);

  useEffect(() => {
    const onDoc = (e) => { if (e.target.closest?.("[data-sonner-toaster]") || e.target.closest?.('[data-testid="notif-panel"]') || e.target.closest?.('[data-testid="notif-big-scrim"]')) return; if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const openItem = async (n) => {
    setOpen(false);
    try { await api.post(`/notifications/${n._id}/read`); } catch { /* ignore */ }
    if (n.kind === "quote_removed" && n.link) navigate(n.link);
    else if (n.quote_id) navigate(`/quotes/${n.quote_id}/edit`);
    else if (n.link === "#training") window.dispatchEvent(new Event("t2-training-open"));
    load();
  };

  const dismiss = async (e, n) => {
    e.stopPropagation();
    setItems((prev) => prev.filter((x) => x._id !== n._id));
    setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
    try { await api.post(`/notifications/${n._id}/dismiss`); } catch { /* ignore */ }
  };

  const markAll = async () => {
    setUnread(0);
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`relative inline-flex items-center justify-center w-9 h-9 rounded-lg border border-white/10 text-slate-300 hover:text-white hover:bg-white/[0.06] transition-colors ${digestPending ? "notif-bell-digest" : unread > 0 ? "notif-bell-live" : ""}`}
        aria-label="Notifications"
        data-testid="notif-bell"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[17px] h-[17px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center ring-2 ring-[#0F1A30]"
            data-testid="notif-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && big && createPortal(
        <>
          <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm" onClick={() => { setOpen(false); setBig(false); }} data-testid="notif-big-scrim" />
          <div className="fixed inset-x-3 top-16 bottom-4 sm:inset-x-[6vw] lg:inset-x-[12vw] rounded-2xl bg-[#0F1A30] border border-white/15 shadow-[0_30px_90px_rgba(0,0,0,0.6)] overflow-hidden z-[81] flex flex-col"
            data-testid="notif-panel" data-size="large">
            <InboxV2 mode="popup" expanded onToggleSize={() => setBig(false)} onClose={() => { setOpen(false); setBig(false); }} onCount={setUnread} />
          </div>
        </>, document.getElementById("root") || document.body)}
      {open && !big && (
        <div
          className={big
            ? "fixed inset-x-3 top-16 bottom-4 sm:inset-x-[6vw] lg:inset-x-[12vw] rounded-2xl bg-[#0F1A30] border border-white/15 shadow-[0_30px_90px_rgba(0,0,0,0.6)] overflow-hidden z-50 flex flex-col"
            : "fixed left-2 right-2 top-14 sm:absolute sm:left-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-[600px] rounded-xl bg-[#0F1A30] border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.5)] overflow-hidden z-50"}
          data-testid="notif-panel" data-size={big ? "large" : "small"}
        >
          <InboxV2 mode="popup" expanded={false} onToggleSize={() => setBig(true)} onClose={() => { setOpen(false); setBig(false); }} onCount={setUnread} />
        </div>
      )}
    </div>
  );
}
